import Vue from 'vue';
import VueRouter, { NavigationGuardNext, Route, RouteConfig } from 'vue-router';
import Login from '@/components/views/Login.vue';
import RunsTableView from '@/components/views/RunsTableView.vue';
import RunsTableViewMode from '@/core/enums/runsTableViewMode';
import store from '@/store';
import { loadLocaleForVehiclesTypes } from '@/i18n';
import { LelyAuth } from '@hn-work/frontend-vue-shared';
import AssignedRuns from '@/components/views/AssignedRuns.vue';
import { knowledgeBaseUrl, newAppAlarmUrl } from '@/core/newAppUrl';

Vue.use(VueRouter);

/** A route query value is a string, or an array of them when repeated. */
function queryValue(value: string | (string | null)[] | null | undefined): string | undefined {
    const single = Array.isArray(value) ? value[0] : value;

    return single || undefined;
}

const routes: Array<RouteConfig> = [
    {
        path: '/',
        name: 'open',
        component: RunsTableView,
        props: {
            view: RunsTableViewMode.OPEN,
        },
    },
    {
        path: '/current-runs/open',
        redirect: '/',
    },
    {
        path: '/assigned',
        name: 'assigned',
        component: AssignedRuns,
        props: true,
    },
    {
        path: '/10-days',
        name: '10-days',
        component: RunsTableView,
        props: {
            view: RunsTableViewMode.TEN_DAYS,
        },
    },
    {
        path: '/archive',
        name: 'archive',
        component: RunsTableView,
        props: {
            view: RunsTableViewMode.ARCHIVED,
        },
    },
    {
        path: '/tickets',
        props: true,
        component: { render: (h) => h('router-view') },
        children: [
            {
                path: 'unassigned',
                name: 'tickets.unassigned',
                component: () =>
                    import('@/components/views/tickets/TicketsView.vue'),
                props: {
                    view: 'unassigned',
                },
            },
            {
                path: 'assigned',
                name: 'tickets.assigned',
                component: () =>
                    import('@/components/views/tickets/TicketsView.vue'),
                props: {
                    view: 'assigned',
                },
            },
            {
                path: 'closed',
                name: 'tickets.closed',
                component: () =>
                    import('@/components/views/tickets/ClosedTicketsView.vue'),
            },
        ],
    },
    {
        path: '/settings',
        name: 'settings',
        component: () => import('@/components/views/SettingsView.vue'),
        children: [
            {
                path: 'rules',
                name: 'settings.rules',
                component: () =>
                    import('@/components/views/settings/RulesView.vue'),
            },
            {
                path: 'timetable',
                name: 'settings.timetable',
                component: () =>
                    import('@/components/views/settings/TimetableView.vue'),
            },
            {
                path: 'phones',
                name: 'settings.phones',
                component: () =>
                    import('@/components/views/settings/PhonesView.vue'),
                meta: {
                    admin: true,
                },
            },
            {
                path: 'browserlogs',
                name: 'settings.browserlogs',
                component: () =>
                    import('@/components/views/settings/BrowserLogsView.vue'),
                meta: {
                    admin: true,
                },
            },
            {
                path: 'notification-types',
                name: 'settings.notification-types',
                component: () =>
                    import(
                        '@/components/views/settings/NotificationTypesView.vue',
                    ),
                meta: {
                    admin: true,
                },
            },
        ],
    },
    {
        path: '/vehicles',
        name: 'vehicles',
        component: () => import('@/components/views/Vehicles.vue'),
    },
    {
        path: '/time-registrations',
        name: 'time-registrations',
        component: () => import('@/components/views/TimeRegistrationHistoryView.vue'),
    },
    {
        path: '/heatmap',
        name: 'heatmap',
        component: () =>
            import('@/components/views/statistics/HeatmapView.vue'),
    },
    {
        // The knowledge base moved to its own app, whose routes are these without
        // the `/knowledge-base` prefix: old links and bookmarks land on the same page
        // there. After the global guard, so a signed-out visitor signs in first.
        path: '/knowledge-base*',
        beforeEnter: (to, from, next) => {
            window.location.href = knowledgeBaseUrl(
                to.path.replace(/^\/knowledge-base/, '') || '/',
                { create: queryValue(to.query.create) },
            );

            return next(false);
        },
    },
    {
        path: '/relay',
        props: true,
        name: 'relay',
        component: () => import('@/components/views/RelayView.vue'),
        redirect: () => ({
            name: 'relay.connections',
        }),
        children: [
            {
                path: 'connections',
                name: 'relay.connections',
                component: () =>
                    import('@/components/views/relay/ConnectionsView.vue'),
            },
            {
                path: 'machines',
                name: 'relay.machines',
                component: () =>
                    import('@/components/views/relay/MachinesView.vue'),
            },
        ],
    },
    // {
    //     path: '/dashboard',
    //     name: 'dashboard',
    //     component: DashboardView,
    // },
    {
        path: '/statistics',
        name: 'statistics',
        component: () => import('@/components/views/StatisticsView.vue'),
        redirect: () => ({
            name: 'statistics.table',
        }),
        children: [
            {
                path: 'table',
                name: 'statistics.table',
                component: () =>
                    import(
                        '@/components/views/statistics/StatisticsTableView.vue',
                    ),
            },
            {
                path: 'heatmap',
                name: 'statistics.heatmap',
                component: () =>
                    import('@/components/views/statistics/HeatmapView.vue'),
            },
        ],
    },
    {
        path: '/zeta-dashboard',
        name: 'zeta-dashboard',
        component: () => import('@/components/views/ZetaDashboardView.vue'),
    },
    {
        path: '/login',
        name: 'login',
        component: Login,
        meta: {
            guest: true,
        },
    },
];

const router = new VueRouter({
    routes,
});

/** Leaves this app for the new Alarm Center, aborting the in-flight navigation. */
function handOverToNewApp(next: NavigationGuardNext, query: Route['query'] = {}): void {
    window.location.href = newAppAlarmUrl(
        queryValue(query.notification),
        queryValue(query.run),
        queryValue(query.vehicleId),
    );

    return next(false);
}

router.beforeEach(async(to, from, next) => {
    if (LelyAuth.isAuthenticated()) {
        await loadLocaleForVehiclesTypes(store.getters.localeKeys, store.getters.vehicleTypesSet);
    }

    store.commit('setPreventMasterDataSync', to.matched.some(route => route.path.includes('settings')));

    if (to.matched.some(route => route.meta.guest)) {
        if (!LelyAuth.isAuthenticated() || !store.state.currentUser) {
            return next();
        } else {
            // Already signed in: the login page has nothing to offer, so hand
            // over to the new Alarm Center like a fresh login does.
            return handOverToNewApp(next);
        }
    }

    if (LelyAuth.isAuthenticated()) {
        if (to.matched.some(route => route.meta.admin)) {
            if (store.getters.isAdmin) {
                return next();
            } else {
                return handOverToNewApp(next);
            }
        }

        // `/` was this app's alarm home, so every way of still reaching it —
        // bookmarks, the logo, the Zeta dashboard's alarm counts, an old
        // `?run=`/`?notification=` link — hands over to the new Alarm Center.
        // The remaining alarm routes (`/assigned`, `/10-days`, `/archive`) stay
        // put as a fallback.
        if (to.path === '/') {
            return handOverToNewApp(next, to.query);
        }

        return next();
    }

    const redirectTo = window.location.href.replace(window.location.origin + '/#', '');

    return next({ name: 'login', query: { redirect_to: redirectTo === '/' ? null : redirectTo } });
});

export default router;
